/* ============================================================================
   Funnel interattivo pet — V2
   ----------------------------------------------------------------------------
   Custom element isolato per istanza.

   Vincoli rispettati:
   - nessuna variabile globale, nessuna funzione globale, nessun onclick inline;
   - lo stato vive in campi d'istanza: due funnel sulla stessa pagina sono
     completamente indipendenti;
   - ogni query parte da `this`, mai da `document` (l'unico uso di `document`
     e' il listener del theme editor, attivo solo in design mode);
   - nessun URL di collezione nel codice: i link sono renderizzati in Liquid
     dal collection picker di ciascun blocco;
   - nessun magic number di scroll: l'invito allo scorrimento e' un <a href>;
   - la guardia customElements.get() rende innocuo un doppio caricamento del
     file (stesso pattern di assets/product-form.js, assets/cart.js).
   ============================================================================ */

if (!customElements.get('funnel-interattivo-v2')) {
  /* Sequenze di passi per percorso. Il risultato non e' una domanda. */
  const PATHS = {
    daily: ['species', 'path', 'age', 'neutered', 'result'],
    vet: ['species', 'path', 'condition', 'result'],
  };

  /* Numero di domande per percorso. Finche' il percorso non e' noto si usa il
     valore del percorso piu' lungo come stima provvisoria. */
  const QUESTION_COUNT = { daily: 4, vet: 3 };

  /* Azioni che corrispondono a una scelta (il nome dell'azione coincide con il
     nome del campo di stato e con il nome del pannello, tranne per age). */
  const CHOICE_ACTIONS = ['species', 'path', 'age', 'neutered', 'condition'];

  class FunnelInterattivoV2 extends HTMLElement {
    constructor() {
      super();

      this.state = {
        species: null,
        path: null,
        age: null,
        neutered: null,
        condition: null,
        stepIndex: 0,
      };

      /* Al primo render non si sposta il focus, per non rubarlo al
         caricamento della pagina. */
      this.shouldMoveFocus = false;
      this.resolvedPanel = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('[data-fv2-action][data-fv2-value]'));
      this.stepPanels = Array.from(this.querySelectorAll('[data-fv2-step]'));
      this.resultPanels = Array.from(this.querySelectorAll('[data-fv2-result]'));
      this.noMappingPanel = this.querySelector('[data-fv2-no-mapping]');
      this.counter = this.querySelector('[data-fv2-counter]');
      this.bar = this.querySelector('[data-fv2-bar]');
      this.backButton = this.querySelector('[data-fv2-action="back"]');
      this.summary = this.querySelector('[data-fv2-summary]');
      this.liveRegion = this.querySelector('[data-fv2-live]');

      this.announcementTemplate = this.dataset.announcement || '';
      this.progressTemplate = this.dataset.progress || '';
      this.progressDone = this.dataset.progressDone || '';

      this.addEventListener('click', this.handleClick);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    /* ------------------------------------------------------------- Derivazioni */

    /* La sequenza dei passi e' una funzione pura del percorso scelto: da qui
       derivano gratuitamente il pulsante Indietro e il conteggio del progresso. */
    get sequence() {
      if (this.state.path && PATHS[this.state.path]) return PATHS[this.state.path];
      return ['species', 'path'];
    }

    get currentStep() {
      return this.sequence[this.state.stepIndex] || 'species';
    }

    get totalQuestions() {
      return QUESTION_COUNT[this.state.path] || QUESTION_COUNT.daily;
    }

    /* Il passo "age" ha due pannelli distinti, uno per specie, cosi' le
       diciture restano renderizzate in Liquid dalle impostazioni e il
       JavaScript non scrive mai testo di etichetta. */
    panelNameFor(step) {
      if (step !== 'age') return step;
      return this.state.species === 'cat' ? 'age-cat' : 'age-dog';
    }

    panelFor(step) {
      const name = this.panelNameFor(step);
      return this.stepPanels.find((panel) => panel.getAttribute('data-fv2-step') === name) || null;
    }

    /* --------------------------------------------------------------- Interazione */

    handleClick(event) {
      const trigger = event.target.closest('[data-fv2-action]');
      if (!trigger || !this.contains(trigger)) return;

      const action = trigger.getAttribute('data-fv2-action');

      if (action === 'back') {
        this.goBack();
        return;
      }

      if (action === 'restart') {
        this.restart();
        return;
      }

      if (CHOICE_ACTIONS.indexOf(action) === -1) return;

      this.choose(action, trigger.getAttribute('data-fv2-value'));
    }

    choose(field, value) {
      if (!value) return;

      if (field === 'species') this.setSpecies(value);
      else if (field === 'path') this.setPath(value);
      else this.state[field] = value;

      this.state.stepIndex = Math.min(this.state.stepIndex + 1, this.sequence.length - 1);
      this.shouldMoveFocus = true;
      this.render();
    }

    /* Invalidazione a cascata: scatta solo se il valore cambia davvero, cosi'
       ri-cliccare la stessa opzione non azzera le risposte successive. */
    setSpecies(value) {
      if (value !== this.state.species) {
        this.state.age = null;
        this.state.neutered = null;
        this.state.condition = null;
      }
      this.state.species = value;
    }

    setPath(value) {
      if (value !== this.state.path) {
        if (value === 'vet') {
          this.state.age = null;
          this.state.neutered = null;
        } else {
          this.state.condition = null;
        }
      }
      this.state.path = value;
    }

    goBack() {
      if (this.state.stepIndex === 0) return;
      this.state.stepIndex -= 1;
      this.shouldMoveFocus = true;
      this.render();
    }

    restart() {
      this.state.species = null;
      this.state.path = null;
      this.state.age = null;
      this.state.neutered = null;
      this.state.condition = null;
      this.state.stepIndex = 0;
      this.shouldMoveFocus = true;
      this.render();
    }

    /* ------------------------------------------------------------------- Render */

    render() {
      const step = this.currentStep;
      const activePanel = this.panelFor(step);

      this.stepPanels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      this.syncSelections();
      this.syncProgress(step);

      if (this.backButton) this.backButton.hidden = this.state.stepIndex === 0;

      if (step === 'result') this.renderResult();
      else this.hideResultPanels();

      this.announce(step, activePanel);

      if (this.shouldMoveFocus && activePanel) {
        this.shouldMoveFocus = false;
        activePanel.focus({ preventScroll: true });
      }
    }

    syncSelections() {
      this.options.forEach((option) => {
        const field = option.getAttribute('data-fv2-action');
        if (CHOICE_ACTIONS.indexOf(field) === -1) return;
        const selected = this.state[field] === option.getAttribute('data-fv2-value');
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    syncProgress(step) {
      const total = this.totalQuestions;
      const current = Math.min(this.state.stepIndex + 1, total);
      const done = step === 'result';

      if (this.counter) {
        this.counter.textContent = done
          ? this.progressDone
          : this.fillTemplate(this.progressTemplate, { current: current, total: total });
      }

      if (this.bar) {
        this.bar.style.width = (done ? 100 : (current / total) * 100) + '%';
      }
    }

    hideResultPanels() {
      this.resultPanels.forEach((panel) => {
        panel.hidden = true;
      });
      if (this.noMappingPanel) this.noMappingPanel.hidden = true;
      if (this.summary) this.summary.textContent = '';
      this.resolvedPanel = null;
    }

    /* Chiave composita: il separatore ":" non puo' comparire negli slug. */
    get mappingKey() {
      const s = this.state;
      if (s.path === 'daily') return [s.species, 'daily', s.age, s.neutered].join(':');
      if (s.path === 'vet') return [s.species, 'vet', s.condition].join(':');
      return '';
    }

    renderResult() {
      this.hideResultPanels();

      const key = this.mappingKey;

      /* Ricerca scopata all'istanza. Se due blocchi condividono la stessa
         chiave vince il primo nell'ordine dei blocchi. Un blocco senza
         collezione configurata non e' un match: si mostra il messaggio. */
      const match = this.resultPanels.find(
        (panel) =>
          panel.getAttribute('data-key') === key &&
          panel.getAttribute('data-has-collection') === 'true'
      );

      if (this.summary) this.summary.textContent = this.buildSummary();

      if (match) {
        match.hidden = false;
        this.resolvedPanel = match;
      } else if (this.noMappingPanel) {
        this.noMappingPanel.hidden = false;
        this.resolvedPanel = this.noMappingPanel;
      }
    }

    /* Il riepilogo usa le etichette visibili lette dal DOM (che arrivano dalle
       impostazioni della sezione), mai gli slug tecnici, e senza alterare
       maiuscole o minuscole. Le etichette dell'eta' vengono lette dal pannello
       della specie corretta. */
    buildSummary() {
      const labels = [];

      CHOICE_ACTIONS.forEach((field) => {
        const value = this.state[field];
        if (!value) return;

        const panel = this.panelFor(field);
        if (!panel) return;

        const option = panel.querySelector('[data-fv2-value="' + value + '"][data-fv2-label]');
        if (!option) return;

        const label = option.getAttribute('data-fv2-label');
        if (label) labels.push(label);
      });

      return labels.join(' · ');
    }

    announce(step, activePanel) {
      if (!this.liveRegion) return;

      if (step === 'result') {
        const heading = this.resolvedPanel
          ? this.resolvedPanel.querySelector('[data-fv2-panel-title]')
          : null;
        this.liveRegion.textContent = heading ? heading.textContent.trim() : this.progressDone;
        return;
      }

      const titleNode = activePanel ? activePanel.querySelector('[data-fv2-step-title]') : null;
      const total = this.totalQuestions;

      this.liveRegion.textContent = this.fillTemplate(this.announcementTemplate, {
        current: Math.min(this.state.stepIndex + 1, total),
        total: total,
        title: titleNode ? titleNode.textContent.trim() : '',
      });
    }

    /* Sostituzione di [current] / [total] / [title]. Il risultato finisce
       sempre in textContent, mai in innerHTML. */
    fillTemplate(template, values) {
      if (!template) return '';
      return Object.keys(values).reduce(
        (output, name) => output.split('[' + name + ']').join(String(values[name])),
        template
      );
    }

    /* ------------------------------------------------------------ Theme editor */

    /* Selezionando un blocco di mappatura nella sidebar, il funnel salta al
       risultato corrispondente. Attivo solo in design mode. */
    handleBlockSelect(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const panel = target.matches('[data-fv2-result]')
        ? target
        : target.closest('[data-fv2-result]');

      if (!panel || !this.contains(panel)) return;

      const parts = (panel.getAttribute('data-key') || '').split(':');
      if (parts.length < 3) return;

      this.state.species = parts[0] || null;
      this.state.path = parts[1] || null;

      if (this.state.path === 'daily') {
        this.state.age = parts[2] || null;
        this.state.neutered = parts[3] || null;
        this.state.condition = null;
      } else {
        this.state.condition = parts[2] || null;
        this.state.age = null;
        this.state.neutered = null;
      }

      this.state.stepIndex = this.sequence.length - 1;
      this.shouldMoveFocus = false;
      this.render();
    }
  }

  customElements.define('funnel-interattivo-v2', FunnelInterattivoV2);
}
